<template>
  <div class="shipping-address-page">
    <header class="address-header">
      <button
        type="button"
        class="back"
        :aria-label="$t('utils.cancel')"
        @click="goBack"
      >
        <van-icon name="arrow-left" />
      </button>
      <h1>{{ $t('shop.shippingAddress') }}</h1>
      <span />
    </header>

    <main class="address-content">
      <section
        v-if="addresses.length"
        class="address-list"
      >
        <article
          v-for="item in addresses"
          :key="item.id"
          class="address-card"
          @click="openEdit(item)"
        >
          <div class="address-card-top">
            <strong>{{ fullName(item) }}</strong>
            <span class="phone">{{ item.phone }}</span>
            <button
              type="button"
              class="delete"
              :aria-label="$t('shop.deleteAddress')"
              @click.stop="removeAddress(item)"
            >
              <van-icon name="delete-o" />
            </button>
          </div>
          <p><van-icon name="location-o" /><span>{{ item.address }}</span></p>
          <p class="city">
            <span /><span>{{ item.city }} ({{ item.post_code }})</span>
          </p>
          <p><van-icon name="envelop-o" /><span>{{ item.email }}</span></p>
          <button
            v-if="item.is_default"
            type="button"
            class="default-tag"
            @click.stop
          >
            {{ $t('shop.defaultAddress') }}
          </button>
          <button
            v-else
            type="button"
            class="set-default"
            @click.stop="setDefault(item)"
          >
            {{ $t('shop.setDefault') }}
          </button>
        </article>
      </section>
      <van-empty
        v-else-if="!loading"
        :description="$t('shop.addressEmpty')"
      />
    </main>

    <footer class="address-footer">
      <button
        type="button"
        @click="openAdd"
      >
        {{ $t('shop.addAddress') }}
      </button>
    </footer>

    <van-popup
      v-model="formVisible"
      round
      position="bottom"
      class="address-popup"
      :close-on-click-overlay="false"
    >
      <div class="address-form-head">
        <button
          type="button"
          @click="closeForm"
        >
          {{ $t('utils.cancel') }}
        </button>
        <h2>{{ editingId ? $t('shop.editAddress') : $t('shop.addAddress') }}</h2>
        <button
          type="button"
          :disabled="saving"
          class="save"
          @click="saveAddress"
        >
          {{ $t('shop.save') }}
        </button>
      </div>
      <form
        class="address-form"
        @submit.prevent="saveAddress"
      >
        <label>{{ $t('shop.firstName') }}<input
          v-model.trim="form.first_name"
          maxlength="80"
          :placeholder="$t('shop.firstName')"
        ></label>
        <label>{{ $t('shop.lastName') }}<input
          v-model.trim="form.last_name"
          maxlength="80"
          :placeholder="$t('shop.lastName')"
        ></label>
        <label>{{ $t('shop.email') }}<input
          v-model.trim="form.email"
          type="email"
          maxlength="160"
          :placeholder="$t('shop.email')"
        ></label>
        <label>{{ $t('shop.phone') }}<input
          v-model.trim="form.phone"
          type="tel"
          maxlength="40"
          :placeholder="$t('shop.phone')"
        ></label>
        <label>{{ $t('shop.city') }}<input
          v-model.trim="form.city"
          maxlength="120"
          :placeholder="$t('shop.city')"
        ></label>
        <label>{{ $t('shop.addressLine') }}<textarea
          v-model.trim="form.address"
          maxlength="500"
          :placeholder="$t('shop.addressLine')"
        /></label>
        <label>{{ $t('shop.postCode') }}<input
          v-model.trim="form.post_code"
          maxlength="40"
          :placeholder="$t('shop.postCode')"
        ></label>
        <van-checkbox
          v-model="form.is_default"
          checked-color="#000"
        >
          {{ $t('shop.defaultAddress') }}
        </van-checkbox>
      </form>
    </van-popup>
  </div>
</template>

<script>
import Vue from 'vue';
import { Checkbox, Dialog, Empty, Icon, Popup, Toast } from 'vant';
import Fetch from '@/utils/fetch';

Vue.use(Checkbox).use(Empty).use(Icon).use(Popup);

const emptyForm = () => ({
    first_name: '', last_name: '', email: '', phone: '', city: '', address: '', post_code: '', is_default: false
});

export default {
    name: 'ShippingAddress',
    data() {
        return {
            addresses: [], loading: false, saving: false, formVisible: false, editingId: 0, form: emptyForm()
        };
    },
    mounted() {
        this.$parent.footer('user', false);
        this.loadAddresses();
    },
    methods: {
        goBack() {
            this.$router.push('/user');
        },
        fullName(item) {
            return [item.first_name, item.last_name].filter(Boolean).join(' ');
        },
        loadAddresses() {
            this.loading = true;
            return Fetch('/shipping_address/index', {}, null, true).then((res) => {
                this.addresses = (res.data && res.data.list) || [];
            }).finally(() => {
                this.loading = false;
            });
        },
        openAdd() {
            this.editingId = 0;
            this.form = emptyForm();
            this.formVisible = true;
        },
        openEdit(item) {
            this.editingId = item.id;
            this.form = {
                first_name: item.first_name || '', last_name: item.last_name || '', email: item.email || '',
                phone: item.phone || '', city: item.city || '', address: item.address || '',
                post_code: item.post_code || '', is_default: !!item.is_default
            };
            this.formVisible = true;
        },
        closeForm() {
            if (!this.saving) this.formVisible = false;
        },
        validateForm() {
            const required = ['first_name', 'last_name', 'email', 'phone', 'city', 'address', 'post_code'];
            if (required.some((name) => !this.form[name])) return false;
            if (!/^\S+@\S+\.\S+$/.test(this.form.email)) return false;
            return /^[0-9+()\-\s]{3,40}$/.test(this.form.phone);
        },
        saveAddress() {
            if (this.saving) return;
            if (!this.validateForm()) {
                Toast(this.$t('shop.addressInvalid'));
                return;
            }
            this.saving = true;
            const data = Object.assign({}, this.form, this.editingId ? { id: this.editingId } : {});
            Fetch('/shipping_address/save', data, null, true).then(() => {
                Toast.success(this.$t('shop.addressSaved'));
                this.formVisible = false;
                return this.loadAddresses();
            }).finally(() => {
                this.saving = false;
            });
        },
        setDefault(item) {
            Fetch('/shipping_address/setDefault', { id: item.id }, null, true).then(() => {
                Toast.success(this.$t('shop.defaultUpdated'));
                return this.loadAddresses();
            });
        },
        removeAddress(item) {
            Dialog.confirm({
                message: this.$t('shop.deleteAddressConfirm'),
                confirmButtonText: this.$t('shop.deleteAddress'),
                cancelButtonText: this.$t('utils.cancel')
            }).then(() => Fetch('/shipping_address/remove', { id: item.id }, null, true)).then(() => {
                Toast.success(this.$t('shop.addressDeleted'));
                return this.loadAddresses();
            }).catch(() => {});
        }
    }
};
</script>

<style scoped>
.shipping-address-page { min-height: 100vh; padding-bottom: 82px; box-sizing: border-box; background: #f5f5f5; color: #333; }
.address-header { display: grid; grid-template-columns: 50px 1fr 50px; height: 50px; align-items: center; background: #fff; border-bottom: 1px solid #ececec; }
.address-header h1 { margin: 0; color: #333; font-size: 16px; font-weight: 500; text-align: center; }
.address-header button { height: 50px; border: 0; background: transparent; color: #ee4d2d; font-size: 23px; }
.address-content { min-height: calc(100vh - 132px); padding: 10px 0; box-sizing: border-box; }
.address-list { display: grid; gap: 10px; }
.address-card { position: relative; padding: 16px; border-radius: 0; background: #fff; box-shadow: none; cursor: pointer; }
.address-card-top { display: flex; min-height: 27px; align-items: center; gap: 12px; padding-right: 26px; }
.address-card strong { overflow: hidden; color: #333; font-size: 15px; text-overflow: ellipsis; white-space: nowrap; }
.address-card .phone { margin-left: auto; color: #667085; font-size: 13px; white-space: nowrap; }
.address-card p { display: grid; grid-template-columns: 20px 1fr; gap: 8px; align-items: start; margin: 9px 0 0; color: #667085; font-size: 13px; line-height: 19px; }
.address-card p .van-icon { color: #ee4d2d; font-size: 16px; }
.address-card .city { margin-top: 1px; }
.delete { position: absolute; top: 16px; right: 16px; border: 0; background: transparent; color: #98a2b3; font-size: 18px; }
.default-tag, .set-default { margin: 12px 0 0 20px; padding: 3px 8px; border: 1px solid #ee4d2d; border-radius: 2px; font-size: 11px; }
.default-tag { background: #fff4f1; color: #ee4d2d; }
.set-default { background: #fff; color: #ee4d2d; }
.address-footer { position: fixed; right: 0; bottom: 0; left: 0; z-index: 2; padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px)); background: #fff; box-shadow: 0 -2px 8px rgba(0, 0, 0, .04); }
.address-footer button { width: 100%; height: 42px; border: 0; border-radius: 4px; background: #ee4d2d; color: #fff; font-size: 14px; }
.address-popup { max-height: calc(100vh - 60px); background: #fff; }
.address-form-head { position: sticky; top: 0; z-index: 1; display: grid; grid-template-columns: 70px 1fr 70px; height: 50px; align-items: center; background: #fff; border-bottom: 1px solid #eee; }
.address-form-head h2 { margin: 0; color: #333; font-size: 16px; text-align: center; }
.address-form-head button { border: 0; background: transparent; color: #667085; font-size: 14px; }
.address-form-head .save { color: #ee4d2d; }
.address-form-head .save:disabled { opacity: .45; }
.address-form { max-height: calc(100vh - 110px); overflow-y: auto; padding: 18px 16px calc(24px + env(safe-area-inset-bottom, 0px)); }
.address-form label { display: block; margin: 0 0 18px; color: #333; font-size: 14px; }
.address-form input, .address-form textarea { display: block; width: 100%; box-sizing: border-box; margin-top: 8px; padding: 0 12px; border: 1px solid #ededed; border-radius: 4px; outline: 0; background: #fff; color: #333; font-size: 14px; }
.address-form input { height: 44px; }
.address-form textarea { min-height: 88px; padding-top: 12px; resize: none; }
.address-form ::v-deep .van-checkbox { margin-top: 5px; color: #3b3b3b; font-size: 14px; }
</style>
